import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Upload, Plus, LogOut, FileText, FolderOpen, DollarSign, HelpCircle } from "lucide-react";
import StaffManagement from "@/components/StaffManagement";
import FAQManagement from "@/components/FAQManagement";
import { NativeSelect } from "@/components/ui/native-select";

interface Category {
  id: string;
  name: string;
  sort_order: number;
}

interface Document {
  id: string;
  title: string;
  description: string | null;
  file_path: string;
  file_name: string;
  created_at: string;
  category_id: string;
}

function getOAuthErrorHelp(code: string, description: string): { title: string; steps: string[] } {
  const desc = description.toLowerCase();
  if (desc.includes("aadsts50194") || desc.includes("multi-tenant")) {
    return {
      title: "Azure App Not Multi-Tenant",
      steps: [
        "Go to Azure Portal → Entra ID → App registrations → your app → Authentication",
        "Under 'Supported account types', select 'Multiple Entra ID tenants'",
        "Choose 'Allow all tenants' (not 'Allow only certain tenants')",
        "Click Save, then retry sign-in",
      ],
    };
  }
  if (desc.includes("unable to exchange external code") || code === "unexpected_failure") {
    return {
      title: "Token Exchange Failed",
      steps: [
        "Go to Azure Portal → App registrations → your app → Certificates & secrets",
        "Verify your client secret has not expired — create a new one if needed",
        "Copy the new secret Value (not the Secret ID)",
        "Go to Supabase Dashboard → Auth → Providers → Azure and paste the new secret",
        "Ensure the Redirect URI in Azure is exactly: https://content.abbashouse.org/auth/v1/callback",
        "Retry in an incognito/private browser window",
      ],
    };
  }
  if (desc.includes("bad_oauth_state") || code === "bad_oauth_state") {
    return {
      title: "OAuth State Mismatch",
      steps: [
        "This usually means the browser session expired during login",
        "Clear your cookies for this site and try again",
        "Use an incognito/private window to retry",
      ],
    };
  }
  if (desc.includes("redirect") || desc.includes("reply url")) {
    return {
      title: "Redirect URI Mismatch",
      steps: [
        "Go to Azure Portal → App registrations → your app → Authentication → Redirect URIs",
        "Ensure this exact URI is listed: https://content.abbashouse.org/auth/v1/callback",
        "Remove any incorrect URIs and click Save",
        "In Supabase Dashboard → Auth → URL Configuration, ensure abbashouse.org/** is in Redirect URLs",
      ],
    };
  }
  return {
    title: "SSO Authentication Error",
    steps: [
      `Error code: ${code}`,
      description || "No additional details provided",
      "Check Azure App Registration and Supabase Auth Provider settings",
      "Try again in an incognito/private browser window",
    ],
  };
}

export default function MissionControl() {
  const { user, isAdmin, isStaff, loading, roleLoading, signIn, signOut } = useAuth();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const [categories, setCategories] = useState<Category[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [newCatName, setNewCatName] = useState("");

  // Upload form
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploadCatId, setUploadCatId] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fundraising settings
  const [raisedAmount, setRaisedAmount] = useState("");
  const [raisedSaving, setRaisedSaving] = useState(false);

  const hasAccess = isAdmin || isStaff;
  const awaitingRoleResolution = !!user && roleLoading && !hasAccess;
  const [myPermissions, setMyPermissions] = useState<string[]>([]);
  const canManageFaq = isAdmin || myPermissions.includes("manage_faq");

  // Detect OAuth errors from URL
  const [oauthError, setOauthError] = useState<{ code: string; description: string } | null>(null);
  useEffect(() => {
    const url = new URL(window.location.href);
    const errorCode = url.searchParams.get("error_code") || url.searchParams.get("error");
    const errorDesc = url.searchParams.get("error_description") || "";
    if (errorCode || errorDesc) {
      setOauthError({ code: errorCode ?? "unknown", description: decodeURIComponent(errorDesc) });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (hasAccess) {
      fetchCategories();
      fetchDocuments();
      fetchRaisedAmount();
      fetchMyPermissions();
    }
  }, [hasAccess]);

  async function fetchMyPermissions() {
    if (!user) return;
    const { data } = await supabase
      .from("staff_permissions" as any)
      .select("permission")
      .eq("user_id", user.id);
    setMyPermissions(((data as any[]) ?? []).map((r) => r.permission));
  }

  async function fetchRaisedAmount() {
    const { data } = await supabase
      .from("site_settings" as any)
      .select("value")
      .eq("key", "fundraising_raised")
      .single();
    if (data) setRaisedAmount((data as any).value ?? "0");
  }

  async function handleSaveRaised() {
    if (raisedSaving) return;
    setRaisedSaving(true);

    // Strip commas, currency symbols, spaces — keep only digits and decimal point
    const raw = raisedAmount.trim().replace(/[^0-9.]/g, "");
    const value = raw === "" ? "0" : String(parseFloat(raw) || 0);

    try {
      // Use .select() to force Supabase to return a response body — without it
      // the promise can silently hang on some connections.
      const saveQuery = supabase
        .from("site_settings" as any)
        .update({ value, updated_at: new Date().toISOString() } as any)
        .eq("key", "fundraising_raised")
        .select();

      const result = await Promise.race([
        saveQuery as unknown as Promise<any>,
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("Save timed out — please try again.")), 8000)
        ),
      ]);

      if (result?.error) {
        throw new Error(result.error.message);
      }

      // Re-fetch from DB to confirm the save actually persisted
      await fetchRaisedAmount();
      toast({ title: "Fundraising amount updated!" });
    } catch (err: any) {
      toast({ title: "Error saving", description: err?.message ?? "Unknown error", variant: "destructive" });
    } finally {
      setRaisedSaving(false);
    }
  }

  async function fetchCategories() {
    const { data } = await supabase
      .from("document_categories")
      .select("*")
      .order("sort_order");
    if (data) setCategories(data);
  }

  async function fetchDocuments() {
    const { data } = await supabase
      .from("transparency_documents")
      .select("*")
      .order("created_at", { ascending: false });
    if (data) setDocuments(data);
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setAuthLoading(true);
    const { error } = await signIn(email, password);
    if (error) toast({ title: "Login failed", description: error.message, variant: "destructive" });
    setAuthLoading(false);
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Check your email", description: "A password reset link has been sent if that email exists." });
      setShowForgot(false);
    }
    setForgotLoading(false);
  }

  async function handleAddCategory() {
    if (!newCatName.trim()) return;
    const maxOrder = categories.reduce((max, c) => Math.max(max, c.sort_order), 0);
    const { error } = await supabase
      .from("document_categories")
      .insert({ name: newCatName.trim(), sort_order: maxOrder + 1 });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setNewCatName("");
      fetchCategories();
      toast({ title: "Category added" });
    }
  }

  async function handleDeleteCategory(id: string) {
    const { error } = await supabase.from("document_categories").delete().eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      fetchCategories();
      fetchDocuments();
      toast({ title: "Category deleted" });
    }
  }

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!uploadFile || !uploadCatId || !uploadTitle.trim()) return;
    setUploading(true);

    const ext = uploadFile.name.split(".").pop();
    const path = `${crypto.randomUUID()}.${ext}`;

    const { error: storageError } = await supabase.storage
      .from("transparency-docs")
      .upload(path, uploadFile);

    if (storageError) {
      toast({ title: "Upload failed", description: storageError.message, variant: "destructive" });
      setUploading(false);
      return;
    }

    const { error: dbError } = await supabase.from("transparency_documents").insert({
      title: uploadTitle.trim(),
      description: uploadDesc.trim() || null,
      file_path: path,
      file_name: uploadFile.name,
      category_id: uploadCatId,
      uploaded_by: user?.id,
    });

    if (dbError) {
      toast({ title: "Error saving document", description: dbError.message, variant: "destructive" });
    } else {
      setUploadTitle("");
      setUploadDesc("");
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchDocuments();
      toast({ title: "Document uploaded" });
    }
    setUploading(false);
  }

  async function handleDeleteDocument(doc: Document) {
    await supabase.storage.from("transparency-docs").remove([doc.file_path]);
    const { error } = await supabase.from("transparency_documents").delete().eq("id", doc.id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      fetchDocuments();
      toast({ title: "Document deleted" });
    }
  }

  if (loading || awaitingRoleResolution) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Verifying access…</p>
      </main>
    );
  }

  if (!user || !hasAccess) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-secondary px-4">
        <SEO title="Mission Control" description="Admin login for Abba's House Communities." />
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-black tracking-tight">Mission Control</CardTitle>
            <p className="text-sm text-muted-foreground">Admin access only</p>
          </CardHeader>
          <CardContent>
            {oauthError && (
              <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-2">
                <p className="text-sm font-semibold text-destructive flex items-center gap-2">
                  ⚠️ {getOAuthErrorHelp(oauthError.code, oauthError.description).title}
                </p>
                <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                  {getOAuthErrorHelp(oauthError.code, oauthError.description).steps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
                <button
                  onClick={() => setOauthError(null)}
                  className="text-xs text-muted-foreground hover:text-foreground underline mt-1"
                >
                  Dismiss
                </button>
              </div>
            )}
            {user && !hasAccess ? (
              <div className="text-center space-y-4">
                <p className="text-sm text-destructive font-medium">You do not have access privileges.</p>
                <Button variant="outline" onClick={signOut} className="w-full">Sign Out</Button>
              </div>
            ) : showForgot ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-sm text-muted-foreground">Enter your email and we'll send a password reset link.</p>
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input id="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required />
                </div>
                <Button type="submit" className="w-full" disabled={forgotLoading}>
                  {forgotLoading ? "Sending…" : "Send Reset Link"}
                </Button>
                <button type="button" onClick={() => setShowForgot(false)} className="text-sm text-muted-foreground hover:text-foreground transition-colors w-full text-center">
                  Back to Sign In
                </button>
              </form>
            ) : (
              <div className="space-y-4">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full flex items-center justify-center gap-2"
                  onClick={async () => {
                    try {
                      const isCustomDomain =
                        !window.location.hostname.includes("lovable.app") &&
                        !window.location.hostname.includes("lovableproject.com");

                      if (isCustomDomain) {
                        const { data, error } = await supabase.auth.signInWithOAuth({
                          provider: 'azure',
                          options: {
                            redirectTo: window.location.origin + '/mission-control',
                            scopes: 'email profile openid',
                            skipBrowserRedirect: true,
                          },
                        });
                        if (error) throw error;
                        if (data?.url) {
                          const oauthUrl = new URL(data.url);
                          const allowedHosts = ["login.microsoftonline.com", "login.microsoft.com", "vqxumwebdjddvifcrxhb.supabase.co"];
                          if (!allowedHosts.some((host) => oauthUrl.hostname === host)) {
                            throw new Error("Invalid OAuth redirect URL");
                          }
                          window.location.href = data.url;
                        }
                      } else {
                        const { error } = await supabase.auth.signInWithOAuth({
                          provider: 'azure',
                          options: {
                            redirectTo: window.location.origin + '/mission-control',
                            scopes: 'email profile openid',
                          },
                        });
                        if (error) throw error;
                      }
                    } catch (error: any) {
                      toast({ title: "SSO failed", description: error.message, variant: "destructive" });
                    }
                  }}
                >
                  <svg className="h-5 w-5" viewBox="0 0 21 21" fill="none"><rect x="1" y="1" width="9" height="9" fill="#F25022"/><rect x="11" y="1" width="9" height="9" fill="#7FBA00"/><rect x="1" y="11" width="9" height="9" fill="#00A4EF"/><rect x="11" y="11" width="9" height="9" fill="#FFB900"/></svg>
                  Sign in with Microsoft
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
                  <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">or</span></div>
                </div>

                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                  </div>
                  <Button type="submit" className="w-full" disabled={authLoading}>
                    {authLoading ? "Signing in…" : "Sign In"}
                  </Button>
                  <button type="button" onClick={() => setShowForgot(true)} className="text-sm text-muted-foreground hover:text-foreground transition-colors w-full text-center">
                    Forgot password?
                  </button>
                </form>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    );
  }

  // Admin dashboard
  const filteredDocs = selectedCategory
    ? documents.filter((d) => d.category_id === selectedCategory)
    : documents;

  return (
    <main className="min-h-screen bg-secondary">
      <SEO title="Mission Control" description="Admin dashboard for Abba's House Communities." noindex />

      {/* Top bar */}
      <div className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl flex items-center justify-between h-14">
          <h1 className="text-lg font-bold tracking-tight">Mission Control</h1>
          <Button variant="ghost" size="sm" onClick={signOut} className="text-primary-foreground hover:text-primary-foreground/80 hover:bg-primary-foreground/10">
            <LogOut className="h-4 w-4 mr-2" /> Sign Out
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-8 space-y-8">
        {/* Fundraising Amount */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg"><DollarSign className="h-5 w-5" /> Fundraising Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-4">
              <div className="space-y-2 flex-1 max-w-xs">
                <Label>Amount Raised ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={raisedAmount}
                  onChange={(e) => setRaisedAmount(e.target.value)}
                  placeholder="0"
                />
              </div>
              <Button onClick={handleSaveRaised} disabled={raisedSaving}>
                {raisedSaving ? "Saving…" : "Save"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">This updates the progress bar on the homepage in real-time.</p>
          </CardContent>
        </Card>

        {/* Categories Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg"><FolderOpen className="h-5 w-5" /> Document Categories</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="New category name"
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
                className="max-w-xs"
              />
              <Button onClick={handleAddCategory} size="sm"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {categories.map((cat) => (
                <div key={cat.id} className="flex items-center justify-between border border-border rounded-lg px-4 py-2 bg-background">
                  <span className="text-sm font-medium">{cat.name}</span>
                  <button
                    onClick={() => handleDeleteCategory(cat.id)}
                    className="text-muted-foreground hover:text-destructive transition-colors"
                    aria-label={`Delete ${cat.name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Upload Document */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg"><Upload className="h-5 w-5" /> Upload Document</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpload} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} required placeholder="e.g. 2024 IRS Determination Letter" />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <NativeSelect
                  variant="compact"
                  value={uploadCatId}
                  onChange={(e) => setUploadCatId(e.target.value)}
                  required
                >
                  <option value="">Select category…</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </NativeSelect>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Description (optional)</Label>
                <Textarea value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)} placeholder="Brief description…" rows={2} />
              </div>
              <div className="space-y-2">
                <Label>File</Label>
                <Input type="file" ref={fileInputRef} onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)} required accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" />
              </div>
              <div className="flex items-end">
                <Button type="submit" disabled={uploading} className="w-full md:w-auto">
                  {uploading ? "Uploading…" : "Upload Document"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Documents List */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <CardTitle className="flex items-center gap-2 text-lg"><FileText className="h-5 w-5" /> Documents</CardTitle>
              <NativeSelect
                variant="compact"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="h-9 w-auto py-1"
              >
                <option value="">All Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </NativeSelect>
            </div>
          </CardHeader>
          <CardContent>
            {filteredDocs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No documents yet.</p>
            ) : (
              <div className="space-y-3">
                {filteredDocs.map((doc) => {
                  const cat = categories.find((c) => c.id === doc.category_id);
                  const { data: urlData } = supabase.storage.from("transparency-docs").getPublicUrl(doc.file_path);
                  return (
                    <div key={doc.id} className="flex items-start justify-between gap-4 border border-border rounded-lg p-4 bg-background">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate">{doc.title}</p>
                        {doc.description && <p className="text-xs text-muted-foreground mt-1">{doc.description}</p>}
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <span className="text-xs bg-muted px-2 py-0.5 rounded font-medium">{cat?.name ?? "Unknown"}</span>
                          <span className="text-xs text-muted-foreground">{new Date(doc.created_at).toLocaleDateString()}</span>
                          <a href={urlData.publicUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline">View</a>
                        </div>
                      </div>
                      <button onClick={() => handleDeleteDocument(doc)} className="text-muted-foreground hover:text-destructive transition-colors shrink-0" aria-label="Delete document">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* FAQ Management - admins or staff with manage_faq */}
        {canManageFaq && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <HelpCircle className="h-5 w-5" /> FAQ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FAQManagement />
            </CardContent>
          </Card>
        )}

        {/* Staff Management - admin only */}
        {isAdmin && <StaffManagement />}
      </div>
    </main>
  );
}